import React, { useState, useEffect, useRef, useCallback } from 'react';
import NavigationMenu from '../components/NavigationMenu';
import LogoBlanc from '../components/LogoBlanc';

const Phonemes1 = () => {
  const [currentSlide, setCurrentSlide] = useState(1);
  const containerRef = useRef(null);
  const intervalRef = useRef(null);

  // Gestion du slideshow automatique
  const startSlideshow = useCallback(() => {
    intervalRef.current = setInterval(() => {
      setCurrentSlide(prev => (prev >= 4 ? 1 : prev + 1));
    }, 8000);
  }, []);

  // Arrêter le slideshow
  const stopSlideshow = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Scroll horizontal ultra fluide avec easing naturel et vitesse équilibrée
  useEffect(() => {
    let isScrolling = false;
    let scrollTimeout;
    let animationFrame;

    const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

    const handleWheel = (e) => {
      const container = containerRef.current;
      if (!container) return;
      
      // Convertir scroll vertical en horizontal seulement
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        
        // Scroll plus rapide pour un ratio naturel molette/déplacement
        const scrollAmount = e.deltaY * 0.8; // Augmenté de 0.4 à 0.8 pour plus de réactivité
        const currentScrollLeft = container.scrollLeft;
        const targetScrollLeft = currentScrollLeft + scrollAmount;
        
        if (!isScrolling) {
          isScrolling = true;
          container.style.scrollBehavior = 'auto';
          // Ajouter une classe pour les effets visuels
          container.classList.add('scrolling');
        }
        
        // Animation fluide avec easing mais plus rapide
        let startTime;
        let startScrollLeft = container.scrollLeft;
        const duration = 120; // Réduit de 200ms à 120ms pour plus de réactivité
        
        const animateScroll = (currentTime) => {
          if (!startTime) startTime = currentTime;
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);
          
          // Appliquer l'easing
          const easedProgress = easeOutQuart(progress);
          const currentValue = startScrollLeft + (scrollAmount * easedProgress);
          
          container.scrollLeft = currentValue;
          
          if (progress < 1 && container === containerRef.current) {
            animationFrame = requestAnimationFrame(animateScroll);
          }
        };
        
        // Annuler l'animation précédente
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
        
        animationFrame = requestAnimationFrame(animateScroll);
        
        // Reset scroll behavior avec un délai optimisé
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          if (container) {
            container.style.scrollBehavior = 'smooth';
            container.classList.remove('scrolling');
          }
          isScrolling = false;
        }, 200); // Réduit de 300ms à 200ms pour suivre la cadence
      }
    };

    const container = containerRef.current;
    if (container) {
      // Style CSS pour un scroll naturellement fluide
      container.style.scrollBehavior = 'smooth';
      container.style.transition = 'opacity 0.1s ease-out';
      
      container.addEventListener('wheel', handleWheel, { passive: false });
      
      return () => {
        container.removeEventListener('wheel', handleWheel);
        clearTimeout(scrollTimeout);
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      };
    }
  }, []);

  // Démarrer le slideshow au montage
  useEffect(() => {
    startSlideshow();
    return () => stopSlideshow();
  }, [startSlideshow, stopSlideshow]);

  return (
    <>
      {/* Styles CSS intégrés */}
      <style>{`
        .phonemes1-container {
          width: 100%;
          height: 100vh;
          overflow: hidden;
          position: relative;
          background-color: #100827;
          font-family: "Roboto", sans-serif;
        }

        .horizontal-container {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          display: flex;
          overflow-x: auto;
          overflow-y: hidden;
          height: 100vh;
          -ms-overflow-style: none;
          scrollbar-width: none;
          /* Transitions douces pour les interactions */
          transition: filter 0.2s ease-out, transform 0.1s ease-out;
        }

        /* Effet subtil pendant le scroll pour feedback visuel */
        .horizontal-container.scrolling {
          filter: blur(0.5px) brightness(1.02);
          transform: scale(0.999);
        }

        /* Transitions douces sur tous les éléments de contenu */
        .horizontal-container section {
          min-width: 100%;
          height: 100vh;
          display: flex;
          justify-content: center;
          align-items: center;
          background: #100827;
          transition: opacity 0.3s ease-out, transform 0.2s ease-out;
        }

        /* Micro-animations sur les éléments interactifs */
        .slideshow-image {
          position: absolute;
          width: 50%;
          height: auto;
          opacity: 0;
          transition: 
            opacity 3s cubic-bezier(0.4, 0, 0.2, 1),
            transform 0.3s ease-out;
          object-fit: cover;
          will-change: opacity, transform;
        }

        .slideshow-image.show {
          opacity: 1;
          transform: scale(1);
        }

        /* Hover doux sur les éléments de texte */
        .text-bloc {
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .text-bloc:hover {
          transform: translateY(-2px);
        }

        /* Animation douce du lecteur vidéo */
        .video-player {
          width: auto;
          height: 100%;
          aspect-ratio: 16/9;
          max-width: 100%;
          border-radius: 5px;
          background: #000;
          transition: 
            transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
            box-shadow 0.4s ease-out;
          will-change: transform;
        }

        .video-player:hover {
          transform: scale(1.02);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
        }

        .horizontal-container::-webkit-scrollbar {
          display: none;
        }

        .horizontal-container section {
          min-width: 100%;
          height: 100vh;
          display: flex;
          justify-content: center;
          align-items: center;
          background: #100827;
        }

        /* FIRST GRID - Section 1 */
        .first-grid {
          margin-left: 3.5vw;
          margin-top: 7vh;
          width: 86vw;
          height: 76vh;
          display: grid;
          grid-template-columns: 30% 2.5% 2.5% 62.5% 2.5%;
          grid-template-rows: 1fr;
          grid-template-areas: "a b c d e";
          grid-column-gap: 0.5vw;
        }

        .first-grid-bloc:nth-child(1) {
          grid-area: a;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .first-grid-bloc:nth-child(2) {
          grid-area: b;
          opacity: 0.3;
        }

        .first-grid-bloc:nth-child(3) {
          grid-area: c;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .first-grid-bloc:nth-child(4) {
          grid-area: d;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .first-grid-bloc:nth-child(5) {
          grid-area: e;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* SECOND GRID - Section 2 (Videyo) - RETOUR ORIGINAL + VIDEO AMÉLIORÉE */
        .second-grid {
          margin-left: 0vw;
          margin-right: 2vw;
          margin-top: 7vh;
          width: 92vw;
          height: 86vh;
          display: grid;
          grid-template-columns: 67% 33%;
          grid-template-rows: 1fr;
          grid-template-areas: "a b";
          grid-column-gap: 2.5vw;
        }

        .second-grid-bloc {
          grid-area: a;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* THIRD GRID - Section 3 (Texte explicatif) */
        .third-grid {
          margin-top: 7vh;
          width: 70vw;
          height: 76vh;
          display: grid;
          grid-template-columns: 60% 30% 10%;
          grid-template-rows: 1fr;
          grid-template-areas: "a b";
          grid-column-gap: 5vw;
        }

        .third-grid-bloc:nth-child(1) {
          grid-area: a;
        }

        .third-grid-bloc:nth-child(2) {
          background-image: url("/media/TransfigurationVertical30Vertical.jpg");
          border-radius: 100px;
          background-size: cover;
          background-position: center;
          grid-area: b;
        }

        /* TEXTE - RETOUR AUX ESPACEMENTS ORIGINAUX */
        .text-bloc:nth-child(1) {
          background-color: #282452;
          width: 83%;
          height: 26%;
          padding: 5%; /* Retour à la valeur originale */
          margin-bottom: 2vh; /* Retour à la valeur originale */
          margin-left: -4vw;
          border-radius: 25px;
          align-items: center;
          justify-content: center;
        }

        .text-bloc:nth-child(2) {
          margin-top: 6.5%; /* Retour à la valeur originale */
          width: 83%;
          margin-left: 3.4vw;
        }

        .text-bloc:nth-child(3) {
          margin-top: 7.5%; /* Retour à la valeur originale */
          margin-left: 10vw;
          width: 83%;
        }

        .text-bloc:nth-child(4) {
          margin-left: 14.35vw;
        }

        .third-grid-text-bloc:nth-child(1) {
          justify-content: right;
          width: 88%;
          margin-top: 8.2%;
          margin-left: 12%;
        }

        .third-grid-text-bloc:nth-child(2) {
          background-color: #282452;
          padding: 5%; /* Retour à la valeur originale */
          width: 88%;
          margin-top: 10%;
        }

        .third-grid-text-bloc:nth-child(3) {
          width: 88%;
          margin-top: 10%; /* Retour à la valeur originale */
          margin-left: 12%;
        }

        .title {
          font-family: "Roboto", sans-serif;
          font-size: 1.6rem;
          margin-bottom: 4%;
          font-weight: lighter;
          color: white;
          letter-spacing: 0.1rem;
          opacity: 0.85;
        }

        .def {
          font-family: "Roboto", sans-serif;
          font-size: 1.6rem;
          margin-bottom: 4%;
          font-weight: 200;
          color: white;
          letter-spacing: 0.1rem;
          opacity: 0.85;
        }

        .def2 {
          text-align: right;
          color: #aac1f4;
          opacity: 1;
        }

        .def3 {
          width: 58%;
          margin-left: 8.8vw;
          line-height: 2rem;
        }

        .text {
          font-family: "Roboto", sans-serif;
          font-size: 1.6rem;
          font-weight: 200;
          color: white;
          letter-spacing: 0.1rem;
          opacity: 0.85;
        }

        .text-third-bloc {
          font-family: "Roboto", sans-serif;
          font-size: 1.6rem;
          font-weight: 200;
          color: white;
          letter-spacing: 0.1rem;
          opacity: 0.85;
        }

        .italic {
          font-style: italic;
        }

        .c {
          color: #aac1f4;
        }

        /* SLIDESHOW - RETOUR AUX DIMENSIONS ORIGINALES */
        .slideshow-container {
          display: flex;
          height: 70.9vh;
          width: 100%;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .slideshow-image {
          position: absolute;
          width: 50%; /* Retour à la taille originale */
          height: auto;
          opacity: 0;
          transition: opacity 3s ease-in-out;
          object-fit: cover;
        }

        .slideshow-image.show {
          opacity: 1;
        }

        /* LECTEUR VIDÉO - DIMENSIONS CORRIGÉES POUR 16:9 */
        .video-container {
          width: 100%;
          height: 86vh; /* Prend toute la hauteur de la grid */
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
        }

        .video-player {
          width: auto;
          height: 100%; /* Hauteur maximale */
          aspect-ratio: 16/9; /* Maintient le ratio 16:9 */
          max-width: 100%;
          border-radius: 5px;
          background: #000;
        }

        /* TITRE DE PAGE */
        .phonemes1-title {
          position: fixed;
          right: 9%;
          margin-top: 2.1rem;
          width: 50rem;
          z-index: 3;
          text-align: center;
        }

        .phonemes1-title h1 {
          font-size: 4rem;
          font-family: "Roboto", sans-serif;
          font-weight: 100;
          color: #aac1f4;
          letter-spacing: 1rem;
          text-transform: uppercase;
          opacity: 0.7;
          margin: 0;
        }

        /* RESPONSIVE */
        @media only screen and (orientation: portrait) {
          .first-grid {
            margin-left: -2vw;
            width: 90vw;
            grid-template-columns: 40% 2.5% 2.5% 52.5% 2.5%;
          }

          .third-grid {
            width: 80vw;
            height: 76vh;
            grid-template-columns: 60% 40%;
            grid-column-gap: 3.5vw;
            margin-left: -2.5vw;
          }

          .phonemes1-title h1 {
            font-size: 2.5rem;
            letter-spacing: 0.7rem;
            right: 35%;
          }
        }

        @media (max-width: 700px) and (orientation: portrait) {
          .first-grid {
            grid-template-columns: 48% 2.5% 2.5% 45.5% 2.5%;
          }

          .first-grid-bloc:nth-child(4) {
            display: grid;
            grid-template-columns: 100%;
            grid-template-rows: 1fr 1fr 1fr 1fr;
            grid-template-areas: 
              "a"
              "b"
              "c"
              "d";
            row-gap: 1vh;
          }

          .third-grid {
            grid-template-columns: 100%;
            grid-template-areas: "a";
            grid-column-gap: 0vw;
          }

          .phonemes1-title h1 {
            font-size: 2rem;
            letter-spacing: 0.7rem;
            font-weight: 400;
          }
        }

        @media (max-width: 769px) {
          .phonemes1-title {
            display: none;
            opacity: 0;
          }
        }

        @media (max-width: 600px) and (orientation: portrait) {
          .def, .text, .text-third-bloc {
            font-size: 1.4rem;
            line-height: 1.8rem;
          }
        }

        @media (max-width: 1400px) and (orientation: landscape) {
          .text-bloc:nth-child(1) {
            width: 100%;
            margin-left: 0vw;
            margin-top: 3vw;
            border-radius: 4%;
          }

          .text-bloc:nth-child(2), 
          .text-bloc:nth-child(3) {
            width: 100%;
            margin-left: 0vw;
          }

          .third-grid {
            grid-template-columns: 60% 40%;
            grid-column-gap: 5vw;
          }
        }

        @media (max-width: 1200px) and (orientation: landscape) {
          .text-bloc:nth-child(1) {
            margin-top: 13vw;
          }

          .text-bloc:nth-child(3), 
          .text-bloc:nth-child(4) {
            visibility: hidden;
          }

          .third-grid {
            width: 80vw;
          }
        }

        @media (max-width: 1000px) and (orientation: landscape) {
          .first-grid-bloc:nth-child(4) {
            display: grid;
            grid-template-columns: 100%;
            grid-template-rows: 1fr 1fr;
            grid-template-areas: 
              "a"
              "b";
            grid-column-gap: 0.5vw;
          }

          .third-grid-text-bloc:nth-child(1) {
            margin-top: 0%;
          }
        }
      `}</style>

      {/* Navigation et Logo */}
      <NavigationMenu />
      <LogoBlanc />
      
      {/* Titre de la page */}
      <div className="phonemes1-title">
        <h1>phonèmes#1</h1>
      </div>

      {/* Container principal */}
      <div className="phonemes1-container">
        <div ref={containerRef} className="horizontal-container">
          
          {/* Section 1 - Contenu principal */}
          <section>
            <div className="first-grid">
              
              {/* Bloc de texte et définitions */}
              <div className="first-grid-bloc">
                <div className="text-bloc">
                  <p className="title">Phonème :</p>
                  <p className="def">
                    micro unité distinctive du langage que l'on peut combiner ou isoler <br />
                    par segmentation
                  </p>
                  <p className="def def2">- entité sonore pouvant se muer, muter, fusionner en un essaim</p>
                </div>

                <div className="text-bloc">
                  <p className="text">
                    &nbsp;&nbsp;&nbsp;S'intégrant dans une nouvelle série de travaux, cette installation qui a vu le jour à l'occasion de mon DNSEP en octobre 2020 est une première expérience de laboratoire immersif.
                    <br /><br />
                    <span className="italic">Phonèmes</span> déplace mes laboratoires de recherches virtuels et les fragments que je produis dans des espaces où la composition permet de créer des expériences atmosphériques immersives, un basculement qui exalte nos sens.
                    <br /><br />
                    Le dispositif de diffusion est composé de cinq écrans de projection répartis dans l'espace enveloppé d'une pièce sonore spatialisée sur huit haut parleurs.
                  </p>
                </div>

                <div className="text-bloc">
                  <p className="def def2 def3">
                    <span className="c">vidéo reanecment à lire avec des écouteurs ou un casque sur la page suivante</span>
                  </p>
                </div>

                <div className="text-bloc">
                  <p className="def def2 def3">
                    <i className="fas fa-long-arrow-alt-right"></i>
                  </p>
                </div>
              </div>

              {/* Blocs vides pour l'espacement */}
              <div className="first-grid-bloc"></div>
              <div className="first-grid-bloc"></div>

              {/* Bloc slideshow */}
              <div className="first-grid-bloc">
                <div className="slideshow-container">
                  <img 
                    src="/media/Phonemes1DNSEP9jpg.jpg" 
                    alt="Phonemes installation" 
                    className={`slideshow-image ${currentSlide === 1 ? 'show' : ''}`}
                  />
                  <img 
                    src="/media/Phonemes1DNSEP8jpg.jpg" 
                    alt="Phonemes installation" 
                    className={`slideshow-image ${currentSlide === 2 ? 'show' : ''}`}
                  />
                  <img 
                    src="/media/Phonemes1DNSEP1jpg.jpg" 
                    alt="Phonemes installation" 
                    className={`slideshow-image ${currentSlide === 3 ? 'show' : ''}`}
                  />
                  <img 
                    src="/media/Phonemes1DNSEP10jpg.jpg" 
                    alt="Phonemes installation" 
                    className={`slideshow-image ${currentSlide === 4 ? 'show' : ''}`}
                  />
                </div>
              </div>

              {/* Bloc vide pour l'espacement */}
              <div className="first-grid-bloc"></div>
            </div>
          </section>

          {/* Section 2 - Vidéo */}
          <section>
            <div className="second-grid">
              <div className="second-grid-bloc">
                <div className="video-container">
                  <video 
                    className="video-player"
                    controls 
                    preload="auto" 
                    poster="/media/PhonemesReanecmentPoster.jpg"
                    loop
                  >
                    <source src="/media/PhonemesReanecmentFINAL.mp4" type="video/mp4" />
                    Votre navigateur ne supporte pas la lecture vidéo.
                  </video>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 - Texte explicatif */}
          <section>
            <div className="third-grid">
              <div className="third-grid-bloc">
                <div className="third-grid-text-bloc">
                  <p className="text-third-bloc">
                    <span className="media-disappear">L'expérience allie plusieurs travaux : <span className="italic">Éveil, Nuée, Symbiose, Herding Call, Transcendance…</span></span>
                    <br /><br />
                    La bande sonore est un travail à quatre mains réalisée avec Nino Lasne (musicien et technicien son), les captations ayant été réalisées dans son studio : violoncelle, basse, bruitages, chant.
                    <br />
                    La partie chantée s'inspire du kulning, chant d'appel scandinave ancien dont les notes de tête peuvent porter sur des kilomètres, notamment en se propageant sur l'eau.
                  </p>
                </div>

                <div className="third-grid-text-bloc">
                  <p className="text-third-bloc">
                    <span className="italic">Phonèmes #1</span> s'inspire du concept de supra-conscience présent dans le roman <span className="italic">Solaris</span> de Stanislas Lem et représente une écriture possible de la transmutation d'une conscience.
                  </p>
                </div>

                <div className="third-grid-text-bloc">
                  <p className="text-third-bloc">
                    La pièce sonore explore plusieurs états à travers le son en commençant brutalement, passant au travers d'un champ magnétique jusqu'à ce que l'atmosphère se stabilise. Des phonèmes viennent ponctuer l'écoute, glissant, se confondant en une nuée d'échos qui semble douée d'une pensée commune. Un visage se dissout et rejoint ce flux, déferlant dans l'espace au grès d'un courant dont la logique nous échappe. Son essence emprunte différentes formes, sa substance se précise en une entité qui s'éveille. L'expérience s'achève sur un mantra uni alors qu'elle atteint un état de transcendance et de plénitude.
                  </p>
                </div>
              </div>

              <div className="third-grid-bloc"></div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Phonemes1;